import React, { useState, useRef } from 'react'
import { connect } from 'react-redux'
import { compose } from 'redux'
import { withRouter } from 'react-router-dom'
import { Popover } from 'antd'
import { segmentApi } from '@edulastic/api'
import {
  IconAITest,
  IconDefaultTest,
  IconPlusCircle,
  IconSeeAllArrow,
  IconSnapQuizTest,
  IconTestBank,
  IconVideoQuizTest,
  IconSectionAdaptiveTest,
  IconContentConverter,
} from '@edulastic/icons'

import { EduButton } from '@edulastic/common'
import {
  CreateTestButtonWrapper,
  Dropdown,
  DropdownItem,
  SeeAll,
  PopuAndButtonWrapper,
} from './styled'

import {
  enableSectionAdaptiveTestSelector,
  getUserFeatures,
  isGcpsDistrictSelector,
  isPremiumUserSelector,
  isVideoQuizAndAIEnabledSelector,
  isGenAIEnabledSelector,
  isContentConvertEnabledSelector,
} from '../../../src/selectors/user'
import CreateTestModal from './CreateTestModal'
import EduAIQuiz from '../../../AssessmentCreate/components/CreateAITest'
import { CREATE_TEST_SEGMENT_EVENT_NAME } from '../../../AssessmentCreate/components/CreationOptions/constants'
import BulkConvertDrawer from '../../../AssessmentCreate/components/CreateAITest/BulkConvertDrawer'

const createTestLinks = {
  TestLibrary: '/author/tests',
  Scratch: '/author/tests/create',
  Adaptive: {
    pathname: '/author/tests/create',
    state: { hasSections: true },
  },
  VideoQuiz: '/author/vqlibrary',
  SnapQuiz: '/author/tests/snapquiz',
  SeeAll: '/author/tests/select',
  ImportTest: '/author/tests/import',
}

const CreateTestButton = ({
  history,
  isVideoQuizAndAIEnabled,
  enableSectionAdaptiveTest,
  isGenAIEnabled,
  isContentConvertEnabled,
}) => {
  const [isModalVisible, setIsModalVisible] = useState(false)
  const [dropdownVisible, setDropdownVisible] = useState(false)
  const buttonRef = useRef(null)

  const handleOpenModal = () => {
    segmentApi.genericEventTrack('CreateTest:ButtonClick', {
      source: 'Dashboard',
    })
    setIsModalVisible(true)
  }

  const handleCloseModal = () => {
    setIsModalVisible(false)
  }

  const handleSegmentEventAndCloseDropdown = (key) => {
    segmentApi.genericEventTrack(`CreateMenu:${key}`, {
      source: 'Dashboard',
    })
    setDropdownVisible(false)
  }

  const handleNavigation = (key) => {
    handleSegmentEventAndCloseDropdown(key)
    history.push(createTestLinks[key])
  }

  // New modal flow (when feature flag is enabled)
  if (isGenAIEnabled) {
    return (
      <CreateTestButtonWrapper>
        <EduButton
          ref={buttonRef}
          isBlue
          data-cy="dashboardCreateTestButton"
          style={{ padding: '5px' }}
          onClick={handleOpenModal}
        >
          <IconPlusCircle />
          CREATE TEST
        </EduButton>
        <CreateTestModal
          visible={isModalVisible}
          onClose={handleCloseModal}
          triggerButtonRef={buttonRef}
        />
      </CreateTestButtonWrapper>
    )
  }

  // Old dropdown flow (when feature flag is disabled)
  const content = (
    <Dropdown>
      <DropdownItem
        onClick={() => handleNavigation('TestLibrary')}
        testLibrary
        data-cy="testLibrary"
      >
        <IconTestBank /> Use Test Library
      </DropdownItem>
      <DropdownItem
        onClick={() => {
          handleNavigation('Scratch')
        }}
        data-cy="createTestFromScratch"
      >
        <IconDefaultTest width="32px" height="32px" /> Create From Scratch
      </DropdownItem>

      <DropdownItem
        innerStyle={isContentConvertEnabled}
        onClick={() => {
          if (!isContentConvertEnabled) {
            handleNavigation('ImportTest')
          }
          segmentApi.genericEventTrack(
            CREATE_TEST_SEGMENT_EVENT_NAME.TEST_IMPORT_CLICK,
            {
              source: 'Dashboard - Create Test',
            }
          )
        }}
        data-cy="importFromGoogleForm"
      >
        {isContentConvertEnabled ? (
          <BulkConvertDrawer source="dashboardCreateTestDropdown">
            <IconContentConverter width="32px" height="32px" /> Content
            Converter
          </BulkConvertDrawer>
        ) : (
          <>
            <IconContentConverter width="32px" height="32px" /> Content
            Converter
          </>
        )}
      </DropdownItem>

      {isVideoQuizAndAIEnabled && (
        <DropdownItem
          onClick={() => {
            handleNavigation('VideoQuiz')
          }}
          data-cy="videoQuiz"
        >
          <IconVideoQuizTest width="32px" height="32px" /> Video Quiz
        </DropdownItem>
      )}
      <DropdownItem
        innerStyle
        onClick={() => handleSegmentEventAndCloseDropdown('UseAI')}
        data-cy="useAI"
      >
        <EduAIQuiz displayScreen="dashboard" isILPFlow>
          <IconAITest width="32px" height="32px" /> Generate using AI
        </EduAIQuiz>
      </DropdownItem>
      {enableSectionAdaptiveTest && (
        <DropdownItem
          onClick={() => {
            handleNavigation('Adaptive')
          }}
          data-cy="adaptiveTest"
        >
          <IconSectionAdaptiveTest width="32px" height="32px" />
          Multi-Section / Adaptive
        </DropdownItem>
      )}
      <DropdownItem
        onClick={() => {
          handleNavigation('SnapQuiz')
        }}
        data-cy="snapQuiz"
      >
        <IconSnapQuizTest width="32px" height="32px" /> PDF to Test (Snap Quiz)
      </DropdownItem>
      <DropdownItem
        seeAll
        onClick={() => {
          handleNavigation('SeeAll')
        }}
        data-cy="seeAll"
      >
        <SeeAll>
          See All <IconSeeAllArrow />
        </SeeAll>
      </DropdownItem>
    </Dropdown>
  )

  return (
    <PopuAndButtonWrapper>
      <Popover
        getPopupContainer={(triggerNode) => triggerNode.parentNode}
        trigger="click"
        content={content}
        placement="bottomRight"
        visible={dropdownVisible}
        onVisibleChange={(newVisible) => setDropdownVisible(newVisible)}
      >
        <EduButton
          isBlue
          data-cy="dashboardCreateTestButton"
          style={{ padding: '5px' }}
        >
          <IconPlusCircle />
          CREATE TEST
        </EduButton>
      </Popover>
    </PopuAndButtonWrapper>
  )
}

const enhance = compose(
  withRouter,
  connect(
    (state) => ({
      isPremiumsUser: isPremiumUserSelector(state),
      isVideoQuizAndAIEnabled: isVideoQuizAndAIEnabledSelector(state),
      userFeatures: getUserFeatures(state),
      isGcpsDistrictExcludingDemoUsers: isGcpsDistrictSelector(state, true),
      enableSectionAdaptiveTest: enableSectionAdaptiveTestSelector(state),
      isGenAIEnabled: isGenAIEnabledSelector(state),
      isContentConvertEnabled: isContentConvertEnabledSelector(state),
    }),
    null
  )
)

export default enhance(CreateTestButton)
